// Reconnecting WebSocket helper — placeholder for Ship 1.
// Ship 2 wires dyno_snapshot + pq_event_keep/drop handlers through this.
//
// Design notes:
// - Module-level singleton to avoid React StrictMode double-connect in dev.
// - Exponential backoff 1s → 15s on reconnect.
// - Subscribe to "dyno" topic after connect so RPM clients on the same
//   endpoint don't receive 5 Hz traffic.

type Listener = (msg: unknown) => void;

interface DynoWs {
  connect(): void;
  disconnect(): void;
  subscribe(cb: Listener): () => void;
  isConnected(): boolean;
}

let singleton: DynoWs | null = null;

export function getDynoWs(): DynoWs {
  if (singleton) return singleton;

  let ws: WebSocket | null = null;
  let listeners = new Set<Listener>();
  let reconnectDelayMs = 1000;
  const MAX_RECONNECT_MS = 15_000;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  function open() {
    if (stopped) return;
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/api/ws/realtime`;
    ws = new WebSocket(url);

    ws.onopen = () => {
      reconnectDelayMs = 1000;
      try {
        ws?.send(JSON.stringify({ type: "subscribe", topic: "dyno" }));
      } catch {
        // ignore
      }
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        listeners.forEach((l) => {
          try {
            l(msg);
          } catch {
            // listener bugs must not break the socket loop
          }
        });
      } catch {
        // non-JSON frame — ignore for now
      }
    };
    ws.onclose = () => scheduleReconnect();
    ws.onerror = () => ws?.close();
  }

  function scheduleReconnect() {
    if (stopped || reconnectTimer) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      reconnectDelayMs = Math.min(reconnectDelayMs * 2, MAX_RECONNECT_MS);
      open();
    }, reconnectDelayMs);
  }

  singleton = {
    connect: () => {
      if (stopped) stopped = false;
      if (!ws) open();
    },
    disconnect: () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = null;
      ws?.close();
      ws = null;
      listeners.clear();
    },
    subscribe: (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    isConnected: () => ws?.readyState === WebSocket.OPEN,
  };
  return singleton;
}
